import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AnimatedPage from '@/components/AnimatedPage';
import {
  geoEmergencyService,
  GeoEmergencySwitch,
} from '@/services/geoEmergency/geoEmergency.service';
import { useNotification } from '@/contexts/NotificationContext';

const GeoEmergency: React.FC = () => {
  const { showNotification } = useNotification();
  const [items, setItems] = useState<GeoEmergencySwitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<GeoEmergencySwitch | null>(null);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'blocked' | 'active'>('all');
  const filteredItems = items.filter((item) => { const needle = search.trim().toLowerCase(); return (!needle || item.country.name.toLowerCase().includes(needle) || item.country.iso_code.toLowerCase().includes(needle)) && (statusFilter === 'all' || item.is_blocked === (statusFilter === 'blocked')); });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await geoEmergencyService.list();
      setItems(data);
    } catch (error) {
      console.error(error);
      showNotification('Не удалось загрузить статусы рубильников', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openBlockDialog = (item: GeoEmergencySwitch) => {
    setSelected(item);
    setReason('');
    setConfirmed(false);
    setDialogOpen(true);
  };

  const handleToggle = async (item: GeoEmergencySwitch) => {
    if (item.is_blocked) {
      setActionLoading(true);
      try {
        await geoEmergencyService.unblock(item.country.id);
        showNotification(`${item.country.name}: сервис включён`, 'success');
        await loadData();
      } catch (error: any) {
        const message =
          error?.response?.data?.detail || 'Не удалось включить сервис';
        showNotification(message, 'error');
      } finally {
        setActionLoading(false);
      }
      return;
    }
    openBlockDialog(item);
  };

  const handleBlockConfirm = async () => {
    if (!selected || !reason.trim() || !confirmed) return;
    setActionLoading(true);
    try {
      await geoEmergencyService.block(selected.country.id, reason.trim());
      showNotification(`${selected.country.name}: аварийное отключение включено`, 'warning');
      setDialogOpen(false);
      await loadData();
    } catch (error: any) {
      const message =
        error?.response?.data?.detail || 'Не удалось отключить сервис';
      showNotification(message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Аварийное отключение по гео
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Останавливает трафик мерчантов, отключает ставки и завершает сессии трейдеров
          выбранной страны.
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}><TextField fullWidth label="Поиск по стране или коду" value={search} onChange={(event) => setSearch(event.target.value)} /><TextField select label="Статус" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)} sx={{ minWidth: 200 }}><MenuItem value="all">Все</MenuItem><MenuItem value="active">Работают</MenuItem><MenuItem value="blocked">Отключены</MenuItem></TextField><Button onClick={() => { setSearch(''); setStatusFilter('all'); }}>Сбросить</Button></Stack>
        <Stack spacing={2}>
          {filteredItems.map((item) => (
            <Card key={item.id} variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={2}
                >
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="h6">
                        {item.country.name} ({item.country.iso_code})
                      </Typography>
                      <Chip
                        label={item.is_blocked ? 'ОТКЛЮЧЕНО' : 'Работает'}
                        color={item.is_blocked ? 'error' : 'success'}
                        size="small"
                      />
                    </Stack>
                    {item.is_blocked && item.blocked_at && (
                      <Typography variant="body2" color="text.secondary">
                        Отключено: {new Date(item.blocked_at).toLocaleString('ru-RU')}
                        {item.blocked_by ? ` — ${item.blocked_by.email}` : ''}
                      </Typography>
                    )}
                    {item.reason && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Причина: {item.reason}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {item.is_blocked
                        ? `Выключено: ${item.affected.merchant_rates} ставок мерчантов, ${item.affected.traders} трейдеров`
                        : `Будет затронуто: ${item.affected.merchant_rates} ставок мерчантов, ${item.affected.traders} трейдеров`}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color={item.is_blocked ? 'success' : 'error'}
                    disabled={actionLoading}
                    onClick={() => handleToggle(item)}
                  >
                    {item.is_blocked ? 'Включить' : 'Отключить'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>

      <Dialog open={dialogOpen} onClose={() => !actionLoading && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" />
          Подтверждение аварийного отключения
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Будет остановлен весь трафик по {selected?.country.name}, отключены ставки
            мерчантов и трейдеров, завершены сессии трейдеров.
          </Alert>
          <TextField
            fullWidth
            required
            label="Причина отключения"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />{' '}
            Я понимаю последствия этого действия
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={actionLoading}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={actionLoading || !reason.trim() || !confirmed}
            onClick={handleBlockConfirm}
          >
            Отключить
          </Button>
        </DialogActions>
      </Dialog>
    </AnimatedPage>
  );
};

export default GeoEmergency;
